import { useMutation } from '@apollo/client';
import { ToastType } from '@alefeducation/ui-elements-library-core';
import { v4 as uuid } from 'uuid';
import { AdminRole } from './useAddAdminUser';
import {
  CREATE_ADMIN_BY_TENANT,
  CREATE_ADMIN_USER,
  CreateAdminResponse,
  CreateAdminUserInput,
  MembershipsV3Type,
  NextGenMembershipInput,
  PutUser,
} from '../../../apollo/queries/adminUsers/addAdmin.queries';
import { injectAutoHideToastOfType } from '../../../utils/Toast';
import {
  ADMIN_CREATED_SUCCESSFULLY,
  SOMETHING_WENT_WRONG_MESSAGE,
} from '../../../utils/constants';
import {
  AdminGeneralInfo,
  AdminOrg,
} from '../../EditAdminUser/hooks/useGetAdminByIdState';

export const ADD_ADMIN_TOAST_ID = 'add-admin-toast-id';

type Props = {
  onCompleted: () => void;
  client: any;
};

type GetTenantInputMembershipType = {
  tenant: string;
  memberships?: NextGenMembershipInput[];
};
type GetTenantInputMembershipV3Type = {
  tenant: string;
  membershipsV3?: MembershipsV3Type[];
};

type GetTenantInputType =
  | GetTenantInputMembershipType
  | GetTenantInputMembershipV3Type;

export const getTenantInput = (
  role: string,
  tenant: string,
  organisations: AdminOrg[],
): GetTenantInputType => {
  const adminWithAllOrg =
    role === AdminRole.SUPER_ADMIN_ORG || role === AdminRole.EMARKING_ADMIN;
  const generateMemberships = () => {
    if (organisations.length === 0 && adminWithAllOrg) {
      const memberships: NextGenMembershipInput[] = [
        {
          organization: '',
          roles: [role],
        },
      ];
      return memberships;
    }
  };

  const generateMembershipsV3 = () => {
    const membershipsV3: MembershipsV3Type[] = organisations.map(
      (organization) => {
        return {
          organization: organization.code,
          role: role,
        };
      },
    );

    return membershipsV3;
  };

  const input: GetTenantInputType = adminWithAllOrg
    ? {
        tenant,
        memberships: generateMemberships(),
      }
    : {
        tenant,
        ...(generateMembershipsV3().length > 0 && {
          membershipsV3: generateMembershipsV3(),
        }),
      };
  return input;
};

const useAddAdminUserAPI = ({ onCompleted, client }: Props) => {
  const [createAdmin] = useMutation<CreateAdminResponse>(CREATE_ADMIN_USER, {
    onCompleted: (data) => {
      if (data.createAdminUser) {
        injectAutoHideToastOfType(
          ADD_ADMIN_TOAST_ID,
          ADMIN_CREATED_SUCCESSFULLY,
          ToastType.SUCCESS,
        );

        onCompleted();
      }
    },
    onError: (e: any) => {
      adminCreationFailed(e);
    },
  });

  const [createAdminByTenant] = useMutation<CreateAdminResponse>(
    CREATE_ADMIN_BY_TENANT,
    {
      onCompleted: (data) => {
        if (data.createUser) {
          injectAutoHideToastOfType(
            ADD_ADMIN_TOAST_ID,
            ADMIN_CREATED_SUCCESSFULLY,
            ToastType.SUCCESS,
          );

          onCompleted();
        }
      },
      client: client,
      onError: (e: any) => {
        adminCreationFailed(e);
      },
    },
  );

  const adminCreationFailed = (e: any) => {
    const errorData =
      e.graphQLErrors && e.graphQLErrors[0] && e.graphQLErrors[0].data;
    const isEmailExist = errorData && errorData.errorCode === 1005;

    if (!isEmailExist) {
      injectAutoHideToastOfType(
        ADD_ADMIN_TOAST_ID,
        SOMETHING_WENT_WRONG_MESSAGE,
        ToastType.ERROR,
      );
    }
  };

  const generateUserInput = (adminDetails: AdminGeneralInfo) => {
    const { tenant, organisations, role, confirmPassword, ...basicDetails } =
      adminDetails;

    return {
      ...basicDetails,
      roles: [role],
      ...getTenantInput(role, tenant, organisations),
    };
  };

  const addAdmin = (adminDetails: AdminGeneralInfo) => {
    const input: CreateAdminUserInput = generateUserInput(adminDetails);
    createAdmin({
      variables: {
        input,
      },
    });
  };

  const addOrgAdminByTenant = (adminDetails: AdminGeneralInfo) => {
    const input: PutUser = {
      ...generateUserInput(adminDetails),
      enabled: true,
      onboarded: true,
      username: adminDetails.email,
    };
    createAdminByTenant({
      variables: {
        input,
      },
    });
  };

  return {
    addAdmin,
    addOrgAdminByTenant,
  };
};

export default useAddAdminUserAPI;
